import React from 'react';
import {
  Box,
  Heading,
  Text,
  Badge,
  Flex,
  VStack,
  Icon,
  Button,
  Divider,
  Link,
  useToast,
} from '@chakra-ui/react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { format } from 'date-fns';
import {
  FaShoppingCart,
  FaUser,
  FaBoxOpen,
  FaMoneyBillAlt,
} from 'react-icons/fa';
import axios from 'axios';
import jsPDF from 'jspdf';
import logo from '../../images/logo.png';
import {
  getRewardOrderTotalPoints,
  isRewardOrder,
} from '../../utils/rewardOrderUtils';
import { CIL_POINTS_LABEL, formatCilPoints } from '../../utils/cilPoints';
import SiblingDiscountBadge from './SiblingDiscountBadge';
import AdminOverrideBugNotice from './AdminOverrideBugNotice';
import MerchOrderFulfillmentTimeline from '../Merch/MerchOrderFulfillmentTimeline';
import { formatMerchShippingAddress } from '../../utils/merchFulfillmentLabels';
import {
  downloadMerchOrderReceiptFromOrder,
  isMerchOrderRecord,
} from '../../utils/merchOrderReceiptPdf';
import { downloadOrderReceiptPdf } from '../../utils/orderReceiptPdf';

const DettaglioOrdineTimeline = ({
  order,
  orders,
  onHandleSaldo,
  onHandleMainPagaOra,
}) => {
  const toast = useToast();
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'succeeded':
        return 'green';
      case 'failed':
        return 'red';
      case 'processing':
        return 'orange';
      case 'pending':
        return 'yellow';
      case 'refunded':
        return 'purple';
      default:
        return 'gray';
    }
  };

  const isReward = isRewardOrder(order);
  const rewardTotalPoints = isReward ? getRewardOrderTotalPoints(order) : 0;

  const magicKeywords = ['magic', 'draft', 'pauper', 'modern', 'commander'];
  const titolo = order.event?.Nome?.toLowerCase() || '';

  const isMagic = magicKeywords.some((kw) => titolo.includes(kw));
  const isBambini =
    order.event?.Partecipanti === 'Per Ragazzi' ||
    (order.event?.categoria || '').toLowerCase().includes('bambini');

  // Frasi ispirazionali
  const frasiGioco = [
    'Alce Rossa – Ogni giocatore appendere un cartellino con nome,\nattraversa il campo avversario, ruba un oggetto dalla base nemica\nsenza farti leggere il nome (pena tornare indietro a cambiarlo).',
    'Anfore – I bambini sono in coppie immobili; a segnale,\nuna coppia “scoppia” e uno insegue l’altro.\nIl fuggitivo si salva attaccandosi a braccetto a un’altra coppia.',
    'Attacco Indiano – Un capo indiano fuori da un cerchio\nlancia un pallone per colpire chi è dentro.\nChi viene colpito esce e diventa aiutante del capo.',
    'Api, Volpi, Uccelli – Tre squadre in triangolo: le Api catturano le Volpi,\nle Volpi gli Uccelli, gli Uccelli le Api.\nChi tocca un avversario lo accompagna in “prigione.”',
    'Amebe – Il gruppo forma cerchi tenendosi per mano;\na segnale, ogni squadra cerca di inglobare un avversario\nscavalcando i “capi” del cerchio.',
    'Battaglia Navale – Campo diviso in due con birilli in fondo.\nCon i palloni lanciati dalla linea di mezzeria,\nle squadre cercano di abbattere i birilli avversari.',
    'Baseball – Un battitore colpisce un lancio e corre verso tre basi.\nLa squadra avversaria lo elimina colpendolo con la palla\nquando è fuori da una base per segnare un punto.',
    'Bowling con Bottiglie – Circa 20 bottiglie piene d’acqua come birilli.\nIn coppie, un bendato calcia le bottiglie guidato a voce dal pilota;\nvince chi ne abbatte di più.',
    'Calcio Ugandese – Non c’è palla fisica:\nchi la “possiede” tiene il dito alzato e passa solo indietro nominando un compagno.\nPer rubare il possesso basta scalpare il giocatore toccato.',
    'CIL – Tutti contro tutti: chi ha la palla può fare 3 passi\ne tentare di colpire un avversario.\nSe vieni preso, siedi in prigione finché non colpisci chi ti ha preso.',
  ];

  const frasiMagic = [
    'Oh, essere un drago… della misura di un baco da seta o immenso…',
    "E si spezza intorno all'ovest cupo",
    'Esistono qualità – alcune cose incorporate, che hanno una doppia vita,\nche così diventa un’entità gemella nata da materia e luce,\nmanifesta in solido e in ombra.',
    "…Non c'era traccia di nulla su quel volto illuminato…",
    "«Sai, ho sempre pensato che gli unicorni fossero mostri favolosi anch'essi? Non ne avevo mai visto uno vivo prima!»\n«Ebbene, ora che ci siamo visti», disse l'unicorno, «se tu crederai in me, io crederò in te.»",
    "Se t'azzardi a stuzzicare un ratto fino alla follia,\npersino un ratto può tormentarti…",
    'Lamenti, fremiti, ed ecco si sollevarono,\nné parlarono, né mossero gli occhi;\nsarebbe stato strano, persino in un sogno,\nveder quegli uomini morti risorgere.',
    'E tra i cumuli, le scogliere imbiancate\ninviarono un riverbero lugubre;\nné forme di uomini, né bestie intravvedemmo –\nil ghiaccio era tutto intorno.',
    "Girotondi impazziti di luci spettrali\ndanzavano di notte;\nl'acqua, come oli di strega,\nar dava verde, azzurra e bianca.",
    'È un talismano contro ogni male, finché mi obbedisci.',
    'E gli spiriti impuri uscirono, ed entrarono nei porci;\ne il gregge si scosse con violenza…',
    "Per creare una prateria ci vuole un trifoglio e un'ape,\nun trifoglio, e un'ape,\ne la rêverie.",
    '…e le acque si fecero muro alla loro destra e alla loro sinistra.',
    'Molti sono in alto luogo e di gran fama:\nma i misteri sono rivelati agli umili.',
    "Com'è noioso fermarsi, porre fine,\narrugginire senza mai splendere nell'uso,\ncome se respirare fosse vita!",
    'Anche il Leviatano! Puoi catturarlo con un amo\no legare una lenza alla sua lingua?',
    "Quando i cimiteri sbadigliano e l'inferno stesso\nespira contagio su questo mondo.",
    "Cos'altro, se il caos attira tutte le forze\nverso l'interno per plasmare un singolo foglio.",
    'Gli dèi usarono le loro piume per dipingere\ntutti i colori del mondo.',
    "Il mago scintilla gridò,\ninvocando l'ira delle tempeste della sua giovinezza.",
  ];

  const frasiCIL = [
    'Creatività in Libertà nasce per dare voce a chi sogna ad alta voce.',
    'CIL è dove il tempo libero diventa tempo prezioso.',
    'Dietro ogni laboratorio, c’è una scintilla che accende il pensiero.',
    'CIL ha dato vita a più di 120 ore di creatività libera nel primo anno.',
  ];

  let fraseFinale;
  // Controlla se è un prodotto
  const isProduct =
    order.product && (order.product.nome || order.product.attributes?.nome);
  // Controlla se è un tesseramento (non evento e non prodotto)
  const isTesseramento = !order.event?.Nome && !isProduct;

  if (isProduct) {
    fraseFinale = 'Grazie per aver acquistato da CIL!';
  } else if (isTesseramento) {
    fraseFinale = 'Grazie per esserti Tesserat* a CIL!';
  } else if (isBambini) {
    fraseFinale = frasiGioco[Math.floor(Math.random() * frasiGioco.length)];
  } else if (isMagic) {
    fraseFinale = frasiMagic[Math.floor(Math.random() * frasiMagic.length)];
  } else {
    fraseFinale = frasiCIL[Math.floor(Math.random() * frasiCIL.length)];
  }

  const isProductSaldato = (product) => {
    return orders.find(
      ({ metadata, documentId, socio }) =>
        documentId !== order.documentId &&
        socio?.documentId === order?.socio?.documentId &&
        metadata?.products.find(
          (p) => p.id === product.id && p.paymentType === 'saldo'
        )
    );
  };

  const getBase64ImageFromURL = async (url) => {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handlePrintOrderDetails = async () => {
    if (!order) return;

    if (isMerchOrderRecord(order)) {
      await downloadMerchOrderReceiptFromOrder(order);
      return;
    }

    // Eventi / CE / tesseramenti: ricevuta consolidata (anti-bug, acconto+saldo)
    if (!isReward) {
      try {
        const orderId = order.documentId || order.id;
        const { data } = await axios.get(
          `/api/ordini/${encodeURIComponent(orderId)}/receipt-payload`,
          { withCredentials: true }
        );
        await downloadOrderReceiptPdf(data.receiptPayload);
      } catch (error) {
        console.error('Errore download ricevuta consolidata:', error);
        toast({
          title: 'Errore',
          description:
            error?.response?.data?.message ||
            'Impossibile generare la ricevuta. Riprova più tardi.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
      return;
    }

    // Carica il logo in base64 (solo premi / reward)
    let logoData = '';
    try {
      logoData = await getBase64ImageFromURL(logo);
    } catch (error) {
      console.error('Errore nel caricamento del logo:', error);
    }

    // eslint-disable-next-line new-cap
    const doc = new jsPDF('p', 'pt', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const lineHeight = 20;
    let y = margin;

    // COLORI CIL
    const giallo = '#ffcc00';
    const blu = '#36a9e1';
    const violaScuro = '#3b235a';

    // HEADER GIALLO
    doc.setFillColor(giallo);
    doc.rect(0, 0, pageWidth, 135, 'F');
    if (logoData) {
      doc.addImage(logoData, 'PNG', pageWidth - margin - 100, 20, 100, 35);
    }
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.setFont('helvetica', 'bold');
    doc.text('CIL – Creatività in Libertà APS', margin, 30);
    doc.setFont('helvetica', 'normal');
    doc.text('CF/P.IVA: 94090260012', margin, 45);
    doc.text('Sede Legale: Via GB Buschetti 18, 10023 Chieri (TO)', margin, 60);
    doc.text(
      'Sede Operativa: Via Carlo Alberto 7, 10023 Chieri (TO)',
      margin,
      75
    );
    doc.text('Email: info@cilassociazione.it', margin, 90);
    doc.text('Telefono: +39 353 468 2493', margin, 105);
    doc.text('www.cilassociazione.it', margin, 120);

    y = 135 + 20;
    // Linea blu
    doc.setLineWidth(1);
    doc.setDrawColor(blu);
    doc.line(margin, y, pageWidth - margin, y);
    y += 30;

    // Titolo
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(violaScuro);
    doc.text('Ricevuta di Pagamento', pageWidth / 2, y, { align: 'center' });
    y += 40;

    // --- PREPARO I DATI DA STAMPARE ---
    const { socio, metadata } = order;
    const {
      id,
      nomeSocio,
      cognomeSocio,
      email,
      codiceFiscaleSocio,
      // eslint-disable-next-line camelcase
      Numero_Tessera,
      indirizzoVia,
      indirizzoCap,
      indirizzoCitta,
      nomeGenitore,
      cognomeGenitore,
      codiceFiscaleGenitore,
    } = socio || {};

    const products = Array.isArray(metadata?.products) ? metadata.products : [];

    // Determina il tipo di ordine e il titolo
    const productName =
      order.product && (order.product.nome || order.product.attributes?.nome);
    const eventName = order.event?.Nome;

    let eventTitle;
    let eventLabel;

    if (productName) {
      eventTitle = productName;
      eventLabel = 'Prodotto';
    } else if (eventName) {
      eventTitle = eventName;
      eventLabel = 'Evento';
    } else {
      eventTitle = `Tesseramento ${new Date().getFullYear()}`;
      eventLabel = 'Prodotto';
    }

    const orderDetailRows = [
      ['Ricevuta N:', order.documentId || id || ''],
      [
        'Data',
        order.createdAt
          ? new Intl.DateTimeFormat('it-IT', {
              dateStyle: 'short',
              timeStyle: 'short',
            }).format(new Date(order.createdAt))
          : '',
      ],
      [
        isReward ? 'Tipo' : eventLabel,
        isReward ? 'Catalogo premi' : eventTitle,
      ],
      [
        'Metodo',
        order.paymentMethod === 'punti'
          ? CIL_POINTS_LABEL
          : order.paymentMethod || '',
      ],
      [
        'Stato',
        isReward
          ? 'Riscatto completato'
          : order.paymentStatus === 'succeeded'
            ? 'Pagamento Effettuato'
            : order.paymentStatus === 'pending'
              ? 'Da Saldare'
              : order.paymentStatus || '',
      ],
      [
        'Totale',
        isReward
          ? formatCilPoints(rewardTotalPoints)
          : order.price
            ? `${parseFloat(order.price).toFixed(2)} €`
            : 'Gratuito',
      ],
    ];

    const socioRows = isReward
      ? orderDetailRows
      : [
          ['Nome Socio', `${nomeSocio || ''}`.trim()],
          ['Cognome Socio', `${cognomeSocio || ''}`.trim()],
          ['Email', email || ''],
          ['Codice Fiscale', codiceFiscaleSocio || ''],
          // eslint-disable-next-line camelcase
          ['N° Tessera', Numero_Tessera || ''],
          [
            'Indirizzo',
            [indirizzoVia, indirizzoCap, indirizzoCitta]
              .filter(Boolean)
              .join(', '),
          ],
          ...orderDetailRows,
        ];

    const parentRows =
      !isReward && (nomeGenitore || cognomeGenitore || codiceFiscaleGenitore)
        ? [
            ['Nome Genitore', `${nomeGenitore || ''}`.trim()],
            ['Cognome Genitore', `${cognomeGenitore || ''}`.trim()],
            ['Codice Fiscale Genitore', codiceFiscaleGenitore || ''],
          ]
        : [];

    // --- STAMPO I DATI SOCIO E GENITORE ---
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0);

    // Socio
    socioRows.forEach(([label, value], i) => {
      doc.text(`${label}:`, margin, y + i * lineHeight);
      doc.text(value.toString(), margin + 140, y + i * lineHeight);
    });
    y += socioRows.length * lineHeight;

    // Sezione Genitore
    if (parentRows.length) {
      y += 10;
      doc.setFont('helvetica', 'bold');
      doc.text('Dati Genitore', margin, y);
      y += lineHeight;
      doc.setFont('helvetica', 'normal');

      parentRows.forEach(([label, value], i) => {
        doc.text(`${label}:`, margin, y + i * lineHeight);
        doc.text(value, margin + 140, y + i * lineHeight);
      });
      y += parentRows.length * lineHeight;
    }

    // … tutto il codice precedente inalterato …

    // --- STAMPO LA SEZIONE PRODOTTI ---
    if (products.length) {
      y += 20;
      doc.setFont('helvetica', 'bold');
      doc.text('Prodotti Acquistati', margin, y);
      y += lineHeight;
      doc.setFont('helvetica', 'normal');

      products.forEach((prod, idx) => {
        // Determina il nome da mostrare
        let displayName;
        if (prod.type === 'variant') {
          displayName = prod.name;
        } else if (prod.type === 'product') {
          // Per i prodotti, usa il nome del prodotto
          displayName = prod.name || productName || eventTitle;
        } else {
          // Per eventi o altri tipi, usa eventTitle
          displayName = eventTitle;
        }

        // Per le varianti, usa prezzoVariante se disponibile, altrimenti amount
        const price =
          prod.type === 'variant' && prod.prezzoVariante !== undefined
            ? prod.prezzoVariante
            : prod.amount || prod.totalAmount;

        const line = `${displayName}${prod.paymentType ? ` (${prod.paymentType})` : ''}: ${parseFloat(price || 0).toFixed(2)} €`;
        doc.text(`• ${line}`, margin + 10, y + idx * lineHeight);
      });

      y += products.length * lineHeight;
    }

    // … continua con separatore, frase ispirazionale e footer …

    // --- SEPARATORE, FRASE ISPIRAZIONALE E FOOTER ---
    y += 30;
    doc.setDrawColor(blu);
    doc.line(margin, y, pageWidth - margin, y);
    y += 30;

    // Frase ispirazionale
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(11);
    doc.setTextColor('#444444');
    const fraseLines = doc.splitTextToSize(fraseFinale, pageWidth - margin * 2);
    fraseLines.forEach((line, i) => {
      doc.text(line, pageWidth / 2, y + i * lineHeight, { align: 'center' });
    });
    y += fraseLines.length * lineHeight + 30;

    // Seconda linea blu
    doc.setDrawColor(blu);
    doc.line(margin, y, pageWidth - margin, y);
    y += 30;

    // Footer viola
    doc.setFillColor(violaScuro);
    doc.rect(0, pageHeight - 50, pageWidth, 50, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor('#ffffff');
    doc.text(
      'Grazie per aver scelto CIL – Creatività in Libertà APS',
      pageWidth / 2,
      pageHeight - 25,
      { align: 'center' }
    );

    // Salvataggio
    doc.save(`ricevuta_ordine_${order.documentId}.pdf`);
  };

  const paymentStatusColor = getPaymentStatusColor(order.paymentStatus);
  const ordersDaSaldare = order.metadata?.products?.filter(
    ({ paymentType }) => paymentType === 'acconto'
  );
  const merchFulfillment = order.metadata?.merch;
  const isMerchOrder = isMerchOrderRecord(order);

  return (
    <Box
      p={{ base: 3, md: 6 }}
      borderBottomLeftRadius="lg"
      borderBottomRightRadius="lg"
      boxShadow="lg"
      bg="blue.50"
      mt={-2}
      borderLeft="1px solid #aaaaaa"
      borderRight="1px solid #aaaaaa"
      borderBottom="1px solid #aaaaaa"
      borderColor="blue.400"
    >
      <VStack align="start" spacing={6} position="relative">
        <AdminOverrideBugNotice order={order} />

        {/* Timeline Item 1: Dettagli Ordine */}
        <Flex pt={3}>
          <Flex flexDirection="column" mr={4}>
            <Icon as={FaShoppingCart} boxSize={6} color="blue.500" />
          </Flex>
          <Box>
            <Heading as="h4" size="sm" mb={2} color="yellow.800">
              Dettagli Ordine
            </Heading>
            <Text fontSize="sm" mb={1}>
              <strong>Data:</strong>{' '}
              {format(new Date(order.createdAt), 'dd/MM/yyyy - hh:mm')}
            </Text>
            <Text fontSize="sm" mb={1}>
              <strong>Numero Ricevuta:</strong> {order.documentId}
            </Text>
            {isReward ? (
              <>
                <Text fontSize="sm" mb={1}>
                  <strong>{CIL_POINTS_LABEL} spesi:</strong>{' '}
                  {formatCilPoints(rewardTotalPoints)}
                </Text>
                <Text fontSize="sm" mb={1}>
                  <strong>Metodo:</strong> {CIL_POINTS_LABEL}
                </Text>
                <Text fontSize="sm" mb={1}>
                  <strong>Stato riscatto:</strong> Completato
                </Text>
              </>
            ) : order.price && order.price !== 0 ? (
              <>
                {isMerchOrder && merchFulfillment?.shippingFee > 0 ? (
                  <>
                    <Text fontSize="sm" mb={1}>
                      <strong>Prodotto:</strong> €
                      {(
                        merchFulfillment.productTotal ??
                        order.price - merchFulfillment.shippingFee
                      ).toFixed(2)}
                    </Text>
                    <Text fontSize="sm" mb={1}>
                      <strong>Spedizione:</strong> €
                      {merchFulfillment.shippingFee.toFixed(2)}
                    </Text>
                    <Text fontSize="sm" mb={1}>
                      <strong>Totale:</strong> €{order.price.toFixed(2)}
                    </Text>
                  </>
                ) : (
                  <Text fontSize="sm" mb={1}>
                    <strong>Prezzo:</strong> €{order.price.toFixed(2)}
                  </Text>
                )}
                <Text fontSize="sm" mb={1}>
                  <strong>Metodo:</strong> {order.paymentMethod}
                </Text>

                {/* Stato + pulsante: stack su mobile, affiancati su desktop */}
                <Flex
                  direction={{ base: 'column', md: 'row' }}
                  align={{ base: 'flex-start', md: 'center' }}
                >
                  <Flex align="center">
                    <Text fontSize="sm" mr={2}>
                      <strong>Stato:</strong>
                    </Text>
                    <Badge colorScheme={paymentStatusColor}>
                      {order.paymentStatus === 'succeeded'
                        ? 'Pagamento Effettuato'
                        : order.paymentStatus === 'pending'
                          ? 'Da Saldare'
                          : order.paymentStatus}
                    </Badge>
                  </Flex>

                  <Flex mt={{ base: 2, md: 0 }} ml={{ base: 0, md: 4 }}>
                    {order.paymentStatus === 'pending' && (
                      <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={() => onHandleMainPagaOra(order)}
                      >
                        Paga Ora
                      </Button>
                    )}
                    {order.paymentStatus === 'succeeded' && (
                      <Button
                        size="sm"
                        colorScheme="teal"
                        onClick={handlePrintOrderDetails}
                      >
                        Scarica Ricevuta
                      </Button>
                    )}
                  </Flex>
                </Flex>
              </>
            ) : null}
          </Box>
        </Flex>

        {isMerchOrder && (
          <Flex>
            <Flex flexDirection="column" mr={4}>
              <Icon as={FaBoxOpen} boxSize={6} color="orange.500" />
            </Flex>
            <Box w="full">
              <MerchOrderFulfillmentTimeline merch={merchFulfillment} />
              {merchFulfillment?.fulfillmentMethod === 'shipping' &&
                merchFulfillment?.shippingAddress && (
                  <Box mt={4} p={3} bg="whiteAlpha.600" borderRadius="md">
                    <Text fontSize="sm" fontWeight="semibold" mb={1}>
                      Spedizione a
                    </Text>
                    <Text fontSize="sm" data-clarity-mask="True">
                      {formatMerchShippingAddress(
                        merchFulfillment.shippingAddress
                      )}
                    </Text>
                  </Box>
                )}
            </Box>
          </Flex>
        )}

        {!isReward && (
          <Flex>
            <Flex flexDirection="column" mr={4}>
              <Icon as={FaUser} boxSize={6} color="green.500" />
            </Flex>
            <Box>
              <Heading as="h4" size="sm" mb={2} color="yellow.800">
                Dettagli Socio
              </Heading>
              {order.socio ? (
                <Text fontSize="sm" data-clarity-mask="True">
                  <strong>Nome:</strong> {order.socio.nomeSocio}{' '}
                  {order.socio.cognomeSocio}
                </Text>
              ) : (
                <Text fontSize="sm">Nessun socio associato</Text>
              )}
            </Box>
          </Flex>
        )}

        {/* Timeline Item 3: Prodotti Acquistati */}
        <Flex>
          <Flex flexDirection="column" mr={4}>
            <Icon as={FaBoxOpen} boxSize={6} color="orange.500" />
            {Boolean(ordersDaSaldare.length)}
          </Flex>
          <Box w="full">
            <Heading as="h4" size="sm" mb={2} color="yellow.800">
              {order.metadata?.orderType === 'reward'
                ? 'Premi riscattati'
                : 'Prodotti Acquistati'}
            </Heading>
            {order.metadata?.orderType === 'reward' &&
            Array.isArray(order.metadata?.rewards) ? (
              order.metadata.rewards.map((reward, index) => (
                <span key={reward.rewardId || index}>
                  <Box mt={2}>
                    <Text fontSize="sm" data-clarity-mask="True">
                      {reward.name} × {reward.quantity} —{' '}
                      <b>{formatCilPoints(reward.subtotalPoints)}</b>
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Stato:{' '}
                      <Badge
                        colorScheme={
                          reward.fulfillmentStatus === 'delivered'
                            ? 'green'
                            : 'orange'
                        }
                        ml={1}
                      >
                        {reward.fulfillmentStatus === 'delivered'
                          ? 'Ritirato'
                          : 'Da ritirare'}
                      </Badge>
                    </Text>
                  </Box>
                  {index !== order.metadata.rewards.length - 1 && (
                    <Divider mt={2} mb={2} />
                  )}
                </span>
              ))
            ) : order.metadata?.products ? (
              order.metadata.products.map((product, index) => (
                <span key={product.id}>
                  <Box mt={2}>
                    {order.event ? (
                      <Text fontSize="sm">
                        <Link
                          as={ReactRouterLink}
                          to={`/event/${order.event.urlTitle}`}
                          color="blue.400"
                        >
                          {product.name}
                        </Link>{' '}
                        -{' '}
                        <b>
                          {(() => {
                            // Per le varianti, usa prezzoVariante se disponibile, altrimenti amount
                            const price =
                              product.type === 'variant' &&
                              product.prezzoVariante !== undefined
                                ? product.prezzoVariante
                                : product.amount;
                            return price ? `€${price.toFixed(2)}` : 'Gratuito';
                          })()}
                        </b>
                      </Text>
                    ) : (
                      <Text fontSize="sm" data-clarity-mask="True">
                        {product.name} -{' '}
                        <b>
                          {(() => {
                            // Per le varianti, usa prezzoVariante se disponibile, altrimenti amount
                            const price =
                              product.type === 'variant' &&
                              product.prezzoVariante !== undefined
                                ? product.prezzoVariante
                                : product.amount;
                            return price ? `€${price.toFixed(2)}` : 'Gratuito';
                          })()}
                        </b>
                      </Text>
                    )}
                    {product.paymentType === 'acconto' && (
                      <Text fontSize="xs" mt={1} color="gray.500">
                        Modalità di pagamento:{' '}
                        <Badge colorScheme="orange" ml="2px" mt="-1px">
                          {product.paymentType}
                        </Badge>
                      </Text>
                    )}
                    {product.applyDiscount && (
                      <Box mt={1}>
                        <SiblingDiscountBadge size="2xs" />
                      </Box>
                    )}
                    {product.overriddenByAdmin === true && (
                      <Text
                        fontSize="xs"
                        color="gray.500"
                        mt={1}
                        lineHeight="short"
                      >
                        Possibile addebito mancante su questa settimana
                      </Text>
                    )}
                  </Box>
                  {index !== order.metadata.products.length - 1 && (
                    <Divider mt={2} mb={2} />
                  )}
                </span>
              ))
            ) : (
              <Text fontSize="sm">Nessun prodotto acquistato.</Text>
            )}
          </Box>
        </Flex>

        {/* Timeline Item 4: Saldi da Completare */}
        {order.event && Boolean(ordersDaSaldare.length) && (
          <Flex>
            <Flex flexDirection="column" mr={4}>
              <Icon as={FaMoneyBillAlt} boxSize={6} color="red.500" />
            </Flex>
            <Box w="full">
              <Heading as="h4" size="sm" mb={2} color="yellow.800">
                Prodotti Da Saldare
              </Heading>
              {ordersDaSaldare.map((product) => (
                <Flex
                  key={product.id}
                  align="center"
                  justify="space-between"
                  mt={1}
                >
                  <Text fontSize="sm">{product.name}</Text>
                  {!isProductSaldato(product) ? (
                    <Button
                      ml={4}
                      size="sm"
                      colorScheme="blue"
                      onClick={() => {
                        onHandleSaldo({
                          productId: product.id,
                          socioId: order.socio.documentId,
                          amount: product.amount,
                          applyDiscount: product.applyDiscount,
                          initialEventPrice: product.initialEventPrice,
                        });
                      }}
                    >
                      Paga il Saldo
                    </Button>
                  ) : (
                    <Badge colorScheme="green" fontSize="xs" ml={2}>
                      Pagato
                    </Badge>
                  )}
                </Flex>
              ))}
            </Box>
          </Flex>
        )}
      </VStack>
    </Box>
  );
};

export default DettaglioOrdineTimeline;
